// src/components/modals/ViewTableModal.jsx
import { X, Hash, MapPin, Users, Clock, Calendar, User, CheckCircle, XCircle } from 'lucide-react'
import { Button } from '../ui/Button'
import { AVAILABILITY_CONFIG } from '../../utils/tableModel'

const ViewTableModal = ({ isOpen, onClose, table, onEdit }) => {
  
  // Don't render if not open or no table
  if (!isOpen || !table) return null

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown'
    try {
      return new Date(dateString).toLocaleString()
    } catch {
      return 'Invalid Date'
    }
  }

  // Get branch name
  const getBranchName = () => {
    if (typeof table.branch === 'object' && table.branch.name) {
      return table.branch.name
    }
    if (table.branchDetails && table.branchDetails.name) {
      return table.branchDetails.name
    }
    return 'Unknown Branch'
  }

  // Get branch details
  const getBranchDetails = () => {
    const branch = typeof table.branch === 'object' ? table.branch : table.branchDetails
    if (!branch) return null
    return branch
  }

  // Get creator name
  const getCreatorName = () => {
    if (typeof table.createdBy === 'object' && table.createdBy.username) {
      return table.createdBy.username
    }
    return 'Unknown'
  }

  const branchDetails = getBranchDetails()

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              {/* <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Hash className="h-6 w-6 text-orange-600" />
              </div> */}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Table {table.number}
              </h2>
              {/* <p className="text-sm text-gray-500">Table Details</p> */}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <Hash className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-600">Table Number</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">{table.number}</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <Users className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-600">Capacity</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {table.capacity} {table.capacity === 1 ? 'person' : 'people'}
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <Clock className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-600">Availability</span>
                </div>
                <div className="flex items-center">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    table.availability === 'available' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {AVAILABILITY_CONFIG[table.availability]?.icon} {
                      AVAILABILITY_CONFIG[table.availability]?.label || table.availability
                    }
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  {table.isActive ? (
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500 mr-2" />
                  )}
                  <span className="text-sm font-medium text-gray-600">Status</span>
                </div>
                <p className={`text-lg font-semibold ${
                  table.isActive ? 'text-green-600' : 'text-red-600'
                }`}>
                  {table.isActive ? 'Active' : 'Inactive'}
                </p>
              </div>
            </div>
          </div>

          {/* Branch Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Branch Information</h3>
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-orange-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-1">{getBranchName()}</h4>
                  {branchDetails && (
                    <div className="space-y-1 text-sm text-gray-600">
                      {branchDetails.type && (
                        <p><span className="font-medium">Type:</span> {branchDetails.type}</p>
                      )}
                      {branchDetails.address && (
                        <p><span className="font-medium">Address:</span> {branchDetails.address}</p>
                      )}
                      {branchDetails.phoneNumber && (
                        <p><span className="font-medium">Phone:</span> {branchDetails.phoneNumber}</p>
                      )}
                      {branchDetails.email && (
                        <p><span className="font-medium">Email:</span> {branchDetails.email}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Creation & Update Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Audit Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center mb-2">
                  <Calendar className="h-4 w-4 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-blue-800">Created</span>
                </div>
                <p className="text-sm text-blue-700 mb-1">
                  {formatDate(table.createdAt)}
                </p>
                <div className="flex items-center">
                  <User className="h-3 w-3 text-blue-600 mr-1" />
                  <span className="text-xs text-blue-600">by {getCreatorName()}</span>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center mb-2">
                  <Calendar className="h-4 w-4 text-green-600 mr-2" />
                  <span className="text-sm font-medium text-green-800">Last Updated</span>
                </div>
                <p className="text-sm text-green-700">
                  {formatDate(table.updatedAt)}
                </p>
              </div>
            </div>
          </div>

          {/* Table Summary */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Summary</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 leading-relaxed">
                Table {table.number} is a {table.capacity}-person table located at {getBranchName()}. 
                It is currently <span className="font-medium">
                  {table.availability === 'available' ? 'available for seating' : 'reserved'}
                </span> and is <span className="font-medium">
                  {table.isActive ? 'active' : 'inactive'}
                </span> in the system.
              </p>
            </div>
          </div>

          {/* Quick Stats
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Stats</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">{table.capacity}</div>
                <div className="text-xs text-orange-700">Seats</div>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {table.availability === 'available' ? '' : ''}
                </div>
                <div className="text-xs text-blue-700">Status</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {table.isActive ? '🟢' : '🔴'}
                </div>
                <div className="text-xs text-green-700">Active</div>
              </div>
            </div>
          </div> */}
        </div>

        {/* Actions */}
        <div className="flex justify-end p-6 border-t border-gray-100">
          <Button
            variant="outline"
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Close
          </Button>
          {/* {onEdit && (
            <Button
              onClick={() => {
                onEdit(table)
                onClose()
              }}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
            >
              Edit Table
            </Button>
          )} */}
        </div>
      </div>
    </div>
  )
}

export default ViewTableModal